import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Building2, CalendarDays, BarChart3, Globe, Shield, MessageSquare,
  CreditCard, CheckCircle, ChevronDown, ChevronUp, ArrowRight, Users,
  Smartphone, Zap, Clock, Star, Quote, Brain, TrendingUp, Code
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  { icon: Building2, title: 'Property Management', desc: 'Manage listings, amenities, photos, location, and rates from one dashboard.' },
  { icon: Brain, title: 'AI Dynamic Pricing', desc: 'Smart pricing engine with seasonal, event-based, and ML recommendations. A/B test prices.' },
  { icon: TrendingUp, title: 'Demand Forecasting', desc: 'AI-powered revenue predictions, occupancy forecasts, and booking pace analysis.' },
  { icon: CalendarDays, title: 'Smart Calendar', desc: 'Availability calendar with iCal sync across Airbnb, Booking.com, and more.' },
  { icon: Star, title: 'Guest Reviews & Ratings', desc: 'Collect and showcase guest reviews. Build trust and improve your listing quality.' },
  { icon: Globe, title: 'Channel Manager', desc: 'Connect and sync with major OTAs. One calendar, zero double bookings.' },
  { icon: Code, title: 'Booking Widgets', desc: 'Embed booking widgets on your website. Accept direct bookings without OTA fees.' },
  { icon: BarChart3, title: 'Reports & Analytics', desc: 'RevPAR, ADR, occupancy rates, and booking channel performance KPIs.' },
  { icon: Shield, title: 'Team & Roles', desc: 'Invite team members with role-based access. Owner, Manager, Staff roles.' },
  { icon: CreditCard, title: 'Flexible Payments', desc: 'Accept card payments via Stripe, M-Pesa, and Pesapal across Africa.' },
  { icon: MessageSquare, title: 'Guest Messaging', desc: 'In-app messaging with guest portal links. Automated check-in templates.' },
  { icon: Smartphone, title: 'Mobile App', desc: 'Native mobile apps for hosts and guests. Manage properties, bookings, and messages on the go.' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [randomTestimonials, setRandomTestimonials] = useState([]);
  const [config, setConfig] = useState({ platform_name: 'iHostPMS', tagline: 'Property Management Made Simple', show_stats: true });
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/payments/public/plans`).then(r => r.json()).then(setPlans).catch(() => {});
    fetch(`${API}/api/payments/public/faqs`).then(r => r.json()).then(setFaqs).catch(() => {});
    fetch(`${API}/api/payments/public/config`).then(r => r.json()).then(c => { if (c.platform_name) setConfig(c); }).catch(() => {});
    fetch(`${API}/api/testimonials/public?limit=10`).then(r => r.json()).then(data => {
      const allTestimonials = data.testimonials || [];
      setTestimonials(allTestimonials);
      // Select 3 random testimonials for display
      const shuffled = [...allTestimonials].sort(() => 0.5 - Math.random());
      setRandomTestimonials(shuffled.slice(0, 3));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FF385C] flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg text-gray-900">{config.platform_name}</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-gray-600">
            <a href="#features" className="hover:text-gray-900 transition">Features</a>
            <a href="#pricing" className="hover:text-gray-900 transition">Pricing</a>
            <a href="#testimonials" className="hover:text-gray-900 transition">Testimonials</a>
            <a href="#faq" className="hover:text-gray-900 transition">FAQ</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="nav-login-btn" className="text-sm">Log In</Button>
            <Button size="sm" onClick={() => navigate('/signup')} data-testid="nav-signup-btn" className="bg-[#FF385C] hover:bg-[#E31C5F] text-white rounded-xl text-sm">Get Started</Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF0F3] via-white to-[#F0F7FF]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FF385C]/10 text-[#FF385C] text-xs font-semibold rounded-full mb-6">
              <Zap className="w-3.5 h-3.5" /> Built for Short-Term Rental Hosts
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-[1.1] tracking-tight" data-testid="hero-title">
              Manage Your Properties.<br />
              <span className="text-[#FF385C]">Grow Your Revenue.</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-500 mt-5 max-w-xl leading-relaxed">
              {config.tagline || 'The all-in-one cloud platform for Airbnb hosts and property managers. Bookings, calendars, channels, and guest communication — simplified.'}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button onClick={() => navigate('/signup')} data-testid="hero-cta" className="bg-[#FF385C] hover:bg-[#E31C5F] text-white rounded-xl px-8 h-12 text-sm font-semibold">
                Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="rounded-xl px-6 h-12 text-sm">
                See Features
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-gray-400">
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-500" /> Free trial</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-500" /> No credit card</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-emerald-500" /> M-Pesa & Stripe</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats - Conditionally shown based on config */}
      {config.show_stats !== false && (
        <section className="border-y border-gray-100 bg-gray-50/50" data-testid="stats-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: config.stat_properties || '500+', label: 'Properties Managed' },
              { value: config.stat_bookings || '10K+', label: 'Bookings Processed' },
              { value: config.stat_channels || '6', label: 'OTA Channels' },
              { value: config.stat_uptime || '99.9%', label: 'Uptime' },
            ].map((s, i) => (
              <div key={i}>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{s.value}</p>
                <p className="text-sm text-gray-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Features */}
      <section id="features" className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Everything You Need to Run Your Rentals</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">From property listings to guest check-out, manage your entire operation from one platform.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="group p-5 rounded-2xl border border-gray-100 hover:border-[#FF385C]/20 hover:shadow-lg hover:shadow-[#FF385C]/5 transition-all duration-300" data-testid={`feature-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#FF385C]/10 flex items-center justify-center mb-3 group-hover:bg-[#FF385C] transition-colors">
                  <f.icon className="w-5 h-5 text-[#FF385C] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">{f.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Features Highlight - Positioned before Pricing */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-purple-600 text-white" data-testid="ai-features-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 text-white text-xs font-semibold rounded-full mb-4">
                <Zap className="w-3.5 h-3.5" /> NEW AI-Powered Features
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Maximize Revenue with AI
              </h2>
              <p className="text-white/80 text-lg mb-6">
                Our AI engine analyzes market demand, seasonal patterns, and competitor pricing to help you set the perfect price every night.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                  <span><strong>Dynamic Pricing</strong> - Automatic rate adjustments based on demand</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                  <span><strong>A/B Price Testing</strong> - Test different prices, see what converts</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                  <span><strong>Demand Forecasting</strong> - Predict bookings 90 days ahead</span>
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-300 flex-shrink-0" />
                  <span><strong>Booking Widgets</strong> - Embed on your site, skip OTA fees</span>
                </li>
              </ul>
            </div>
            <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <Brain className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
                  <p className="font-bold text-2xl">15%</p>
                  <p className="text-xs text-white/70">Avg. Revenue Increase</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-300" />
                  <p className="font-bold text-2xl">90</p>
                  <p className="text-xs text-white/70">Days Forecast</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <BarChart3 className="w-8 h-8 mx-auto mb-2 text-blue-300" />
                  <p className="font-bold text-2xl">7+</p>
                  <p className="text-xs text-white/70">KPI Metrics</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <Code className="w-8 h-8 mx-auto mb-2 text-pink-300" />
                  <p className="font-bold text-2xl">3</p>
                  <p className="text-xs text-white/70">Widget Types</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 sm:py-24 bg-gray-50/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Simple, Transparent Pricing</h2>
            <p className="text-gray-500 mt-3">Start with a free trial. Upgrade as you grow. Pay via M-Pesa or Card.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="pricing-grid">
            {plans.map((p, i) => (
              <div
                key={p.plan_id || i}
                className={`relative bg-white rounded-2xl p-6 border transition-all duration-300 hover:shadow-lg ${
                  p.code === 'standard' ? 'border-[#FF385C] ring-2 ring-[#FF385C]/20 shadow-lg' : 'border-gray-200'
                }`}
                data-testid={`pricing-${p.code}`}
              >
                {p.code === 'standard' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-[#FF385C] text-white text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" /> MOST POPULAR
                    </span>
                  </div>
                )}
                <h3 className="text-lg font-bold text-gray-900">{p.name}</h3>
                <div className="mt-3 mb-4">
                  {p.is_contact_us ? (
                    <p className="text-sm text-gray-500 font-medium">Custom Pricing</p>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-gray-900">KES {p.price?.toLocaleString()}</span>
                      <span className="text-sm text-gray-400 ml-1">/mo</span>
                    </>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-4 pb-4 border-b border-gray-100">
                  {p.max_users} user{p.max_users > 1 ? 's' : ''} &middot; {p.max_properties > 100 ? 'Unlimited' : p.max_properties} {p.max_properties > 1 ? 'properties' : 'property'}
                </p>
                <ul className="space-y-2 mb-6">
                  {p.features?.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-xs text-gray-600">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                {p.trial_days > 0 && <p className="text-[10px] text-gray-400 mb-3">{p.trial_days}-day free trial included</p>}
                <Button
                  onClick={() => navigate(p.is_contact_us ? '/signup' : '/signup')}
                  className={`w-full rounded-xl text-sm ${
                    p.code === 'standard'
                      ? 'bg-[#FF385C] hover:bg-[#E31C5F] text-white'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                  data-testid={`pricing-cta-${p.code}`}
                >
                  {p.is_contact_us ? 'Contact Sales' : 'Start Free Trial'}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-400 mt-6">All plans include email notifications, iCal sync, and guest messaging. Prices in Kenyan Shillings (KES).</p>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Get Started in Minutes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Sign Up', desc: 'Create your account and add your first property. Start your free trial instantly.', icon: Users },
              { step: '2', title: 'Connect Channels', desc: 'Link your Airbnb, Booking.com, and other OTA calendars via iCal sync.', icon: Globe },
              { step: '3', title: 'Manage & Grow', desc: 'Handle bookings, message guests, track revenue, and scale your portfolio.', icon: BarChart3 },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#FF385C]/10 flex items-center justify-center mx-auto mb-4">
                  <s.icon className="w-6 h-6 text-[#FF385C]" />
                </div>
                <div className="text-xs font-bold text-[#FF385C] mb-2">STEP {s.step}</div>
                <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faq" className="py-16 sm:py-24 bg-gray-50/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3" data-testid="faq-list">
            {faqs.map((faq, i) => (
              <div key={faq.faq_id || i} className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                >
                  <span className="text-sm font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === i ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-gray-500 leading-relaxed whitespace-pre-wrap">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Host Testimonials - 3 Random Cards Grid */}
      {randomTestimonials.length > 0 && (
        <section id="testimonials" className="py-16 sm:py-24 bg-gradient-to-br from-slate-900 to-slate-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Loved by Property Hosts</h2>
              <p className="text-slate-300 max-w-lg mx-auto">See what property managers and Airbnb hosts are saying about {config.platform_name}</p>
            </div>
            
            {/* 3 Random Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials-grid">
              {randomTestimonials.map((testimonial, idx) => (
                <div 
                  key={testimonial.testimonial_id || idx}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-[#FF385C]/30 transition-all duration-300 hover:scale-[1.02]"
                  data-testid={`testimonial-${idx}`}
                >
                  <Quote className="w-8 h-8 text-[#FF385C] mb-4 opacity-60" />
                  <p className="text-white/90 mb-4 line-clamp-4 leading-relaxed text-sm">{testimonial.content}</p>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-4 h-4 ${i < testimonial.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`} 
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    {testimonial.author_picture ? (
                      <img 
                        src={testimonial.author_picture} 
                        alt={testimonial.author_name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#FF385C] flex items-center justify-center">
                        <span className="text-white font-semibold">
                          {testimonial.author_name?.charAt(0) || 'H'}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-white text-sm">{testimonial.author_name}</p>
                      <p className="text-xs text-slate-400">
                        {testimonial.role || 'Property Host'}
                        {testimonial.company_name && ` · ${testimonial.company_name}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="bg-gray-900 rounded-3xl px-8 py-12 sm:py-16 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ready to Simplify Your Property Management?</h2>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">Join hundreds of hosts who use {config.platform_name} to manage their rentals. Start your free trial today.</p>
            <Button onClick={() => navigate('/signup')} data-testid="cta-signup" className="bg-[#FF385C] hover:bg-[#E31C5F] text-white rounded-xl px-8 h-12 text-sm font-semibold">
              Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Main Footer Grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 pb-12 border-b border-gray-800">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#FF385C] flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-xl">{config.platform_name || 'iHostPMS'}</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                The all-in-one cloud platform for Airbnb hosts and property managers.
              </p>
            </div>
            
            {/* Product Column */}
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-sm text-gray-400">
                <li><a href="#features" className="hover:text-white transition">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Pricing</a></li>
                <li><a href="/marketplace" className="hover:text-white transition">Marketplace</a></li>
                <li><a href="/docs" className="hover:text-white transition">Documentation</a></li>
              </ul>
            </div>
            
            {/* Company Column */}
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-3 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition">About Us</a></li>
                <li><a href="#" className="hover:text-white transition">Careers</a></li>
                <li><a href="#" className="hover:text-white transition">Blog</a></li>
                <li><a href="#" className="hover:text-white transition">Press</a></li>
              </ul>
            </div>
            
            {/* Partners Column */}
            <div>
              <h4 className="font-semibold text-white mb-4">Partners</h4>
              <ul className="space-y-3 text-sm text-gray-400">
                <li><a href="/signup" className="hover:text-white transition">Become a Host</a></li>
                <li><a href="#" className="hover:text-white transition">Become a Partner</a></li>
                <li><a href="#" className="hover:text-white transition">Affiliate Program</a></li>
                <li><a href="/admin/marketplace" className="hover:text-white transition">Admin Portal</a></li>
              </ul>
            </div>
            
            {/* Legal Column */}
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-3 text-sm text-gray-400">
                <li><a href="/terms" className="hover:text-white transition">Terms of Service</a></li>
                <li><a href="/privacy" className="hover:text-white transition">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition">Cookie Policy</a></li>
                <li><a href="#" className="hover:text-white transition">GDPR</a></li>
              </ul>
            </div>
            
            {/* Get the App Column */}
            <div>
              <h4 className="font-semibold text-white mb-4">Get the App</h4>
              <ul className="space-y-3 text-sm text-gray-400">
                <li>
                  <button 
                    onClick={() => {
                      if (window.deferredPrompt) {
                        window.deferredPrompt.prompt();
                        window.deferredPrompt.userChoice.then((choiceResult) => {
                          window.deferredPrompt = null;
                        });
                      } else if (navigator.standalone === false || window.matchMedia('(display-mode: browser)').matches) {
                        alert('To install: tap the share button and select "Add to Home Screen"');
                      } else {
                        alert('App is already installed or installation is not supported');
                      }
                    }}
                    className="hover:text-white transition flex items-center gap-2"
                    data-testid="install-pwa-landing-footer"
                  >
                    <Smartphone className="w-4 h-4" />
                    Install PWA
                  </button>
                </li>
                <li>
                  <a href="https://expo.dev/artifacts/eas/creVoYwAHUoKgMF3a1WFy7.apk" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="hover:text-white transition flex items-center gap-2"
                  >
                    <Smartphone className="w-4 h-4" />
                    Download Android APK
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Bottom Footer */}
          <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Social Links */}
            <div className="flex items-center gap-4">
              {config.social_facebook && (
                <a href={config.social_facebook} target="_blank" rel="noopener noreferrer" 
                   className="w-10 h-10 rounded-full bg-gray-800 hover:bg-[#FF385C] flex items-center justify-center transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/></svg>
                </a>
              )}
              {config.social_twitter && (
                <a href={config.social_twitter} target="_blank" rel="noopener noreferrer"
                   className="w-10 h-10 rounded-full bg-gray-800 hover:bg-[#FF385C] flex items-center justify-center transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
              )}
              {config.social_instagram && (
                <a href={config.social_instagram} target="_blank" rel="noopener noreferrer"
                   className="w-10 h-10 rounded-full bg-gray-800 hover:bg-[#FF385C] flex items-center justify-center transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
              )}
              {config.social_linkedin && (
                <a href={config.social_linkedin} target="_blank" rel="noopener noreferrer"
                   className="w-10 h-10 rounded-full bg-gray-800 hover:bg-[#FF385C] flex items-center justify-center transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              )}
            </div>
            
            {/* Contact Info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
              {config.support_email && (
                <a href={`mailto:${config.support_email}`} className="hover:text-white transition flex items-center gap-1">
                  <MessageSquare className="w-4 h-4" /> {config.support_email}
                </a>
              )}
              {config.support_phone && (
                <span className="flex items-center gap-1">
                  <Smartphone className="w-4 h-4" /> {config.support_phone}
                </span>
              )}
            </div>
            
            {/* Copyright */}
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} {config.platform_name || 'iHostPMS'}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
